import { Alert, Box, Button, Paper, Stack } from '@mui/material';
import { t } from 'i18next';
import { FormEventHandler, useEffect, useState } from 'react';
import { InlineLoader } from '../../common/components/InlineLoader';
import { Dialog, DialogBody } from '../../common/components/Modal/Modal';
import { getErrorMessage } from '../../common/errors/errors';
import { ButtonBox, PrimaryButton, TertiaryButton } from '../components/Buttons';
import { UsersSelectTable } from '../components/SelectUsersTable';
import { User } from '../models/User';
import { OrganizationService } from '../services/OrganizationService';
import { ProjectService } from '../services/ProjectService';

type Props = {
  projectId: number;
  onClosed: (projectId: number | undefined) => void;
};

export const AddTestersToProjectFlow = ({ projectId, onClosed }: Props) => {
  // It's not a preference to have services in states, but we use it here so
  // that we can leverage same service in multiple functionalities.
  const [projectService] = useState(new ProjectService());
  const [testerService] = useState(new OrganizationService());

  const [availableTesters, setAvailableTesters] = useState<User[]>([]);
  const [projectTestersIds, setProjectTestersIds] = useState<Set<number>>(new Set());
  const [selectedAvailableTesters, setSelectedAvailableTesters] = useState<number[]>([]);
  const [selectedProjectTesters, setSelectedProjectTesters] = useState<number[]>([]);

  const [isDataFetched, setIsDataFetched] = useState(false);
  const [loadingSave, setLoadingSave] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchTesters = async () => {
      if (!projectId) return;
      setIsDataFetched(false);
      try {
        const organizationTesters: User[] = await testerService.getTesters();
        const projectTesters: User[] = (await projectService.getProject(projectId)).getTesters();
        setProjectTestersIds(new Set(projectTesters.map((t) => t.getId())));
        setAvailableTesters(organizationTesters);
        setIsDataFetched(true);
      } catch (e: unknown) {
        setError(getErrorMessage(e));
      }
    };
    fetchTesters();

    // Cancel all active requests on projectId change or unmount
    return () => {
      projectService.cancelAllRequests();
      testerService.cancelAllRequests();
    };
  }, [projectId, projectService, testerService]);

  const handleAvailableRowClick = (ids: number[]) => {
    setSelectedAvailableTesters(ids);
    setSelectedProjectTesters([]);
  };

  const handleTesterRowClick = (ids: number[]) => {
    setSelectedProjectTesters(ids);
    setSelectedAvailableTesters([]);
  };

  const handleAddTesters = async () => {
    setProjectTestersIds((prev) => new Set([...prev, ...selectedAvailableTesters]));
    setSelectedProjectTesters([]);
    setSelectedAvailableTesters([]);
  };

  const handleRemoveTesters = async () => {
    setProjectTestersIds((prev) => {
      return new Set([...prev].filter((id) => !selectedProjectTesters.includes(id)));
    });
    setSelectedProjectTesters([]);
    setSelectedAvailableTesters([]);
  };

  const saveTesters: FormEventHandler = async (e) => {
    e.preventDefault();
    try {
      setLoadingSave(true);
      await projectService.updateProjectTesters(projectId, [...projectTestersIds]);
      onClosed(projectId);
    } catch (e: unknown) {
      setError(getErrorMessage(e));
      setLoadingSave(false);
    }
  };

  return (
    <Dialog title={t('addTestersToProject.title')} subtitle={t('addTestersToProject.subtitle')} onDismiss={() => onClosed(undefined)}>
      <DialogBody>
        <Stack spacing={1.5} component="form" onSubmit={saveTesters}>
          {error && <Alert severity="error">{error}</Alert>}
          {!error && !isDataFetched && <InlineLoader />}
          {isDataFetched && (
            <Box display="flex" justifyContent="center" alignItems="center" gap={2} mt={2}>
              <Paper elevation={1} sx={{ height: '16.25rem', width: '100%' }} data-role="organization-testers">
                <UsersSelectTable
                  disabled={loadingSave}
                  header={t('addTestersToProject.availableTesters')}
                  noRowsLabel={t('addTestersToProject.noTestersAvailable')}
                  onSelectionChange={(ids) => handleAvailableRowClick(ids)}
                  rows={availableTesters.filter((t) => !projectTestersIds.has(t.getId()))}
                  selectedRows={selectedAvailableTesters}
                />
              </Paper>
              <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
                <Button
                  aria-label={t('addTestersToProject.addTestersToProject')}
                  disabled={selectedAvailableTesters.length === 0 || loadingSave}
                  onClick={handleAddTesters}
                  size="small"
                  variant="outlined"
                >
                  &gt;
                </Button>
                <Button
                  aria-label={t('addTestersToProject.removeTestersFromProject')}
                  disabled={selectedProjectTesters.length === 0 || loadingSave}
                  onClick={handleRemoveTesters}
                  size="small"
                  variant="outlined"
                >
                  &lt;
                </Button>
              </Box>
              <Paper elevation={1} sx={{ height: '16.25rem', width: '100%' }} data-role="project-testers">
                <UsersSelectTable
                  disabled={loadingSave}
                  header={t('addTestersToProject.assignedTesters')}
                  noRowsLabel={t('addTestersToProject.noTestersAssignedYet')}
                  onSelectionChange={(ids) => handleTesterRowClick(ids)}
                  rows={availableTesters.filter((t) => projectTestersIds.has(t.getId()))}
                  selectedRows={selectedProjectTesters}
                />
              </Paper>
            </Box>
          )}
          {isDataFetched && (
            <ButtonBox>
              <TertiaryButton disabled={loadingSave} onClick={() => onClosed(undefined)} autoFocus>
                {t('addTestersToProject.back')}
              </TertiaryButton>
              <PrimaryButton disabled={loadingSave} type="submit">
                {t('addTestersToProject.submit')}
              </PrimaryButton>
            </ButtonBox>
          )}
        </Stack>
      </DialogBody>
    </Dialog>
  );
};
